import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Cropper from "react-easy-crop";
import { Save, X, AlertTriangle, Camera } from "lucide-react";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, storage } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { generateThumbnail, resizeImage } from "../utils/resizeImage";
import { validateImageFile } from "../utils/imageValidation";
import { cropImageFile } from "../utils/cropImage";

interface BlogProps {
  blogAuthorId?: string;
  onSaveSuccess?: () => void;
  onAuthorAdded?: () => void;
}

const BlogAuthor: React.FC<BlogProps> = React.memo(
  ({ blogAuthorId, onSaveSuccess, onAuthorAdded }) => {
    const [authorName, setAuthorName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(
      null
    );

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [showCropper, setShowCropper] = useState(false);
    const [tempImageUrl, setTempImageUrl] = useState<any>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedArea, setCroppedArea] = useState<any>(null);
    const [loadingCropper, setLoadingCropper] = useState(false);

    // Fetch existing author
    useEffect(() => {
      if (!blogAuthorId) return;

      const fetchData = async () => {
        try {
          const docSnap = await getDoc(doc(db, "blogAuthors", blogAuthorId));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setAuthorName(data.name || "");
            setImagePreview(data.image || null);
            setThumbnailPreview(data.thumbnail || null);
          }
        } catch (err) {
          console.error(err);
          setError("Failed to load author details.");
        }
      };

      fetchData();
    }, [blogAuthorId]);

    const resetImageState = useCallback(() => {
      setSelectedImage(null);
      setImagePreview(null);
      setThumbnailPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    const validateInputs = useCallback(() => {
      if (!authorName.trim()) {
        setError("Name is required");
        return false;
      }
      return true;
    }, [authorName]);

    const handleImageChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (fileInputRef.current) fileInputRef.current.value = "";

        const validationError = validateImageFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        setLoadingCropper(true);
        try {
          const { resizedUrl: resized } = await resizeImage(file);
          setTempImageUrl(resized);
          setShowCropper(true);
          setError(null);
        } finally {
          setLoadingCropper(false);
        }
      },
      []
    );

    const onCropComplete = useCallback((_: any, croppedPixels: any) => {
      setCroppedArea(croppedPixels);
    }, []);

    const handleCropConfirm = useCallback(async () => {
      if (!tempImageUrl || !croppedArea) return;

      const cropped = await cropImageFile(tempImageUrl, croppedArea);
      if (cropped) {
        setSelectedImage(cropped);
        setImagePreview(URL.createObjectURL(cropped));
      }

      setShowCropper(false);
    }, [tempImageUrl, croppedArea]);

    const handleSave = useCallback(async () => {
      if (!validateInputs()) return;
      setIsSaving(true);

      try {
        let imageUrl = null;
        let thumbnailUrl = null;

        if (selectedImage) {
          const timestamp = Date.now();
          const imageRef = ref(
            storage,
            `author/${timestamp}_${selectedImage.name}`
          );
          await uploadBytes(imageRef, selectedImage);
          imageUrl = await getDownloadURL(imageRef);

          const thumbBlob = await generateThumbnail(selectedImage);
          const thumbRef = ref(
            storage,
            `author/thumbnails/${timestamp}_thumb_${selectedImage.name}`
          );
          await uploadBytes(thumbRef, thumbBlob);
          thumbnailUrl = await getDownloadURL(thumbRef);
        } else {
          imageUrl = imagePreview;
          thumbnailUrl = thumbnailPreview;
        }

        const authorData = {
          name: authorName.trim(),
          image: imageUrl,
          thumbnail: thumbnailUrl,
          updatedAt: serverTimestamp(),
        };

        if (blogAuthorId) {
          await updateDoc(doc(db, "blogAuthors", blogAuthorId), authorData);
        } else {
          await addDoc(collection(db, "blogAuthors"), {
            ...authorData,
            createdAt: serverTimestamp(),
          });
          setAuthorName("");
          resetImageState();
        }
        onAuthorAdded?.();
        onSaveSuccess?.();
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to save author. Try again.");
      } finally {
        setIsSaving(false);
      }
    }, [
      authorName,
      selectedImage,
      blogAuthorId,
      onSaveSuccess,
      resetImageState,
      validateInputs,
    ]);

    const imageSection = useMemo(
      () => (
        <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
          <div className="space-y-2 text-center">
            <div className="flex flex-col items-center">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={resetImageState}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <Camera size={48} className="mx-auto text-gray-400" />
              )}
            </div>
            <label
              htmlFor="image-author-upload"
              className="cursor-pointer text-[#18f7b1] hover:text-[#0fd494]"
            >
              {imagePreview ? "Change image" : "Upload an image"}
              <input
                id="image-author-upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="sr-only"
              />
            </label>
            <p className="text-sm text-gray-500">
              PNG, JPG, JPEG, WEBP up to 10MB
            </p>
          </div>
        </div>
      ),
      [imagePreview, handleImageChange, resetImageState]
    );

    return (
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <AlertTriangle className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                placeholder="Enter author name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Author's Image
              </label>
              {imageSection}
            </div>
          </div>
        </div>

        {loadingCropper && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
            <div className="text-white text-xl flex items-center gap-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-white border-opacity-70" />
              Preparing image for cropping...
            </div>
          </div>
        )}
        {showCropper && tempImageUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
            <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
              <Cropper
                image={tempImageUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
              <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                <button
                  onClick={() => setShowCropper(false)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropConfirm}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Crop & Save
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] flex items-center gap-2 transition-all ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black" />
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
);

export default BlogAuthor;
